import React, { useState } from "react"
import { Button, Modal, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleQuestion,
  faTerminal,
  faWandMagicSparkles,
  faShuffle,
  faHardDrive,
  faTriangleExclamation,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { DOCS_LINKS } from "constants/docsLinks"

/**
 * Help button + modal explaining the four DATA(Q) Query Console modes
 * (Console, Builder, Transform, Saved) and how they work together.
 *
 * Client-facing copy: avoid implementation jargon. Each mode gets an icon,
 * an accent-coloured rail, and a short description of what it does and
 * when to use it.
 */
const QueryConsoleHelp: React.FC = () => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Tooltip title="What is each mode? How do they work together?" placement="bottom">
        <Button
          aria-label="Open Query Console help"
          icon={<FontAwesomeIcon icon={faCircleQuestion} />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <Modal
        title={
          <span className="qc-help-title">
            DATA<span className="qc-help-title__paren">(</span>Q
            <span className="qc-help-title__paren">)</span> Query Console
          </span>
        }
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={980}
        className="qc-help-modal"
      >
        <div className="qc-help-content">
          <p className="qc-help-intro">
            The workspace has four modes. Each one is good at a different
            part of the analysis. They share the same project, so you can
            switch between them freely.
          </p>

          <div className="qc-help-grid">
            <Section
              tone="accent"
              icon={faShuffle}
              heading="Transform"
              tagline="Prepare your data"
            >
              <p>
                Build pipelines of data preparation steps: combine answer
                options into NETs, fill blank values, derive subgroup
                metrics, clean up codes for distribution.
              </p>
              <p>
                You can keep multiple pipelines per project. For example,
                one for internal analysis and another tailored for client
                distribution, and switch between them.
              </p>
              <p className="qc-help-when">
                <strong>Use when:</strong> you need derived variables that
                don&rsquo;t exist in the raw data, or you&rsquo;re shaping
                the dataset for export.
              </p>
            </Section>

            <Section
              tone="accent"
              icon={faWandMagicSparkles}
              heading="Builder"
              tagline="Build cross-tabs and data tables visually"
            >
              <p>
                Drag variables into rows and columns, pick the statistics you
                want (n, %, average&hellip;), apply a filter, hit Run. Each
                builder tab is independent. You can keep several side by
                side.
              </p>
              <p>
                A Builder can apply a Transform pipeline before it runs, so
                your derived variables are available automatically. Pick the
                pipeline from the toolbar.
              </p>
              <p className="qc-help-when">
                <strong>Use when:</strong> you want to interrogate the data
                without writing query code.
              </p>
            </Section>

            <Section
              tone="accent"
              icon={faTerminal}
              heading="Console"
              tagline="Write DATA(Q) directly"
            >
              <p>
                Type DATA(Q) statements yourself. Run executes whatever
                you&rsquo;ve written. Use it for ad-hoc queries, exploratory
                work, or when the visual tools don&rsquo;t cover your case.
              </p>
              <p className="qc-help-when">
                <strong>Use when:</strong> you&rsquo;re comfortable with the
                query language and want full control.
              </p>
            </Section>

            <Section
              tone="accent"
              icon={faHardDrive}
              heading="Saved queries"
              tagline="Everything you save lives here"
            >
              <p>
                Pipelines, Builders, and Console queries all save to the
                same project. The saved-queries panel groups them by type.
                Pick one and it opens in its own tab.
              </p>
              <p>
                The query selector shows which is which: a different icon
                next to each name tells you whether it&rsquo;s a pipeline, a
                builder, or a script.
              </p>
            </Section>
          </div>

          <Divider />

          <Section
            tone="warn"
            icon={faTriangleExclamation}
            heading="Good to know"
            tagline=""
          >
            <ul className="qc-help-grid-tight">
              <li>
                <strong>Builder Run is self-contained.</strong> Each run
                starts fresh and re-applies its pipeline, so the result is
                always reproducible.
              </li>
              <li>
                <strong>Transform Apply</strong> runs the active pipeline
                and refreshes the data tree, making new variables available
                to drag into a Builder.
              </li>
              <li>
                <strong>Console runs are not self-contained.</strong> If
                you&rsquo;re using derived fields here, apply the relevant
                Transform pipeline first.
              </li>
              <li>
                <strong>Local edits stay on this device</strong> until you
                press Save. Saving makes a pipeline or builder available
                from any device on the same project.
              </li>
              <li>
                <strong>Renaming and saving</strong> a pipeline or builder
                replaces the old version on the project automatically, with
                no duplicates left behind.
              </li>
            </ul>
          </Section>

          <Divider />

          <div className="qc-help-footer" style={{ textAlign: "center" }}>
            <Button
              type="link"
              onClick={() =>
                window.open(DOCS_LINKS.QUERY_LANGUAGE, "_blank")
              }
            >
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                style={{ marginRight: 6 }}
              />
              Read the full query language documentation
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

interface SectionProps {
  tone?: "accent" | "warn"
  icon: IconDefinition
  heading: string
  tagline: string
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({
  tone = "accent",
  icon,
  heading,
  tagline,
  children,
}) => (
  <section className="qc-help-section" data-tone={tone}>
    <header className="qc-help-section__header">
      <span className="qc-help-section__icon-wrap" aria-hidden>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div>
        <h3 className="qc-help-section__heading">{heading}</h3>
        {tagline && (
          <p className="qc-help-section__tagline">{tagline}</p>
        )}
      </div>
    </header>
    <div className="qc-help-section__body">{children}</div>
  </section>
)

const Divider: React.FC = () => <div className="qc-help-divider" aria-hidden />

export default QueryConsoleHelp
